/**
 * Crafting — the read side of `{ kind: 'recipes' }` (issue #8, decided: build it).
 *
 * Three Salvage unlocks granted recipe ids, `unlockedRecipes()` had zero callers
 * and there was no crafting system at all. `salvage.craft.basic` fires on the
 * first death, at 12 Salvage, and granted nothing — which means the first
 * mechanical reward the Salvage track hands a new player was a no-op.
 *
 * Crafting is a **material sink**, and that is the whole argument for it: 86
 * materials and 104 reactions exist and the player's only relationship with them
 * is "don't stand in that". A sink gives the material simulation an economic
 * role and turns the codex from a reference document into a tool.
 *
 * ## The open sub-question, decided: crafting consumes a CARRIED inventory
 *
 * The issue asked whether inputs come from a carried pouch or from "materials
 * you have seen this run", and recommended carried. Carried it is, for three
 * reasons the "seen" model cannot answer:
 *
 *   1. **A checklist is not a cost.** Against a seen-set, every recipe is either
 *      permanently available or permanently not, and crafting the same item
 *      twice costs exactly as much as crafting it once — nothing. Issue #8's own
 *      acceptance criterion is "crafting consumes materials, and running out
 *      matters", and a set has no quantity to run out of.
 *   2. **It gives the world a reason to be dug.** A pouch makes a coal bank a
 *      resource rather than scenery, which is the loop the falling-sand sim was
 *      built for and has never been asked to serve.
 *   3. **The cap is the design.** `POUCH_CAP` means carrying salt is choosing
 *      not to carry iron. Seen-sets cannot express a trade-off, so every recipe
 *      would be free of opportunity cost as well as free of cost.
 *
 * The cost of the decision, stated plainly: it needs a harvest verb, a pouch on
 * `RunState.inventory`, and a save migration. All three are here or in
 * `runState.ts`. The harvest *call site* is now live, in `combat/harvest.ts`:
 * the Reaper's Harvest scythe pockets what its swing cuts free, which is the
 * class's authored identity ("harvests corpses into pooled BLOOD") rather than
 * a generic on-kill drop. `harvestMaterial` takes a `MaterialPouch`, not a
 * `RunState`, so that call site never has to name a save type.
 *
 * The codex is still load-bearing: `craftingView` hides recipes whose inputs
 * name a material this profile has never recorded, so a recipe you cannot read
 * is not offered — while a recipe you can read but cannot afford *is* offered
 * and refuses with the shortfall. Unknown and unaffordable are different
 * sentences.
 *
 * **Where:** rest nodes (#6), alongside reforge and stash. A workbench you had
 * to find is a better beat than a menu you can open anywhere.
 *
 * Decoupled by design: materials, modules and weapon instances are plain string
 * ids, and `craft` *returns* what to do rather than doing it — installing a
 * module and rerolling a weapon are combat/'s business, and a meta module that
 * reached into `WeaponInstance` would put the save file's lifetime on a combat
 * type. `tests/ids.test.ts` checks every id named here resolves.
 */

import type { Progression } from './progression';
import { addModule } from './runState';
import type { RunState } from './runState';

// ------------------------------------------------------------------- the pouch

/**
 * Total units the pouch holds, across all materials.
 *
 * 240 is about four full stacks. Low enough that carrying salt is choosing not
 * to carry iron — which is the entire reason the cap exists — and high enough
 * that a basic recipe (30 units) is affordable from one good dig rather than
 * three.
 */
export const POUCH_CAP = 240;

/** Per-material ceiling, so one abundant powder cannot fill the pouch alone. */
export const POUCH_STACK_CAP = 60;

/**
 * The only part of a run a harvest touches.
 *
 * `RunState` satisfies this structurally, so nothing in `meta/` changes shape.
 * It exists for the harvest's *caller*: the harvest verb lives in `combat/`
 * (the scythe, below), and a combat module that had to name `RunState` to hand
 * over a handful of cells would put the save file's lifetime on a combat type —
 * the same coupling this file's header refuses in the other direction.
 */
export interface MaterialPouch {
  inventory: { materials: Record<string, number> };
}

export function pouchTotal(run: MaterialPouch): number {
  let n = 0;
  for (const k in run.inventory.materials) n += run.inventory.materials[k];
  return n;
}

export function materialUnits(run: MaterialPouch, material: string): number {
  return run.inventory.materials[material] ?? 0;
}

/**
 * Pick up material. Returns how much was actually taken, which is the number the
 * caller should show — silently discarding the overflow of a full pouch is how a
 * player concludes the harvest verb is broken.
 */
export function harvestMaterial(run: MaterialPouch, material: string, amount: number): number {
  const want = Math.max(0, Math.floor(amount));
  if (want === 0 || !material) return 0;
  const held = materialUnits(run, material);
  const room = Math.min(POUCH_STACK_CAP - held, POUCH_CAP - pouchTotal(run));
  const take = Math.min(want, Math.max(0, room));
  if (take > 0) run.inventory.materials[material] = held + take;
  return take;
}

/**
 * Spend, all-or-nothing. A partial debit on a failed craft would leave the
 * player poorer with nothing to show, which is strictly worse than a refusal —
 * the same rule `spendScrip` keeps, for the same reason.
 */
export function spendMaterials(run: RunState, cost: Readonly<Record<string, number>>): boolean {
  for (const m in cost) if (materialUnits(run, m) < cost[m]) return false;
  for (const m in cost) {
    const left = materialUnits(run, m) - cost[m];
    if (left > 0) run.inventory.materials[m] = left;
    else delete run.inventory.materials[m];
  }
  return true;
}

/** What is short, and by how much. The sentence a refused craft has to produce. */
export function shortfall(
  run: RunState,
  cost: Readonly<Record<string, number>>,
): { material: string; need: number; have: number }[] {
  const out: { material: string; need: number; have: number }[] = [];
  for (const m in cost) {
    const have = materialUnits(run, m);
    if (have < cost[m]) out.push({ material: m, need: cost[m], have });
  }
  return out;
}

// ----------------------------------------------------------------- the recipes

export type RecipeId =
  | 'repair-frame'
  | 'reseat-module'
  | 'transmute-family'
  | 'refine-reagent'
  | 'fuse-weapons'
  | 'splice-trigger';

/** The three unlock tiers, and the three verbs that keep them distinct. */
export type RecipeTier = 'basic' | 'transmute' | 'grafting';

export const TIER_VERB: Readonly<Record<RecipeTier, string>> = {
  // Make · convert · move. Issue #8's second acceptance criterion is that the
  // three tiers do something *distinct*, and three flavours of "make" would
  // satisfy the unlock names while failing the criterion.
  basic: 'make',
  transmute: 'convert',
  grafting: 'move',
};

/**
 * What a completed craft asks the integrator to do.
 *
 * A discriminated union rather than a side effect: the materials are already
 * spent by the time this is returned, and everything left to do lives in
 * `combat/`. `core/game.ts` switches on `kind` and calls the right system.
 */
export type CraftOutcome =
  | { kind: 'module'; moduleId: string }
  | { kind: 'material'; material: string; units: number }
  | { kind: 'reroll'; weaponInstanceId: string }
  | { kind: 'transfer'; fromInstanceId: string; toInstanceId: string; moduleId: string };

export interface RecipeDef {
  id: RecipeId;
  tier: RecipeTier;
  name: string;
  /** One sentence, in the player's language. */
  desc: string;
  /**
   * Material name -> units. Empty `inputs` is not allowed: a recipe that
   * consumes nothing is a button, and this whole file exists to be a sink.
   * Fixed-cost recipes list everything; parameterised ones (`transmute-family`)
   * list only their fixed reagent and read the rest from the arguments.
   */
  inputs: Readonly<Record<string, number>>;
  /** Which of the four outcome kinds it produces. */
  produces: CraftOutcome['kind'];
  /** Fixed-output recipes only. */
  output?: { moduleId?: string; material?: string; units?: number };
  /** Parameterised recipes name which arguments they require. */
  needs?: readonly ('weapon' | 'twoWeapons' | 'module' | 'fromMaterial' | 'toMaterial')[];
}

/**
 * How much of a transmuted material you get back, per unit fed in.
 *
 * A third, and the loss is the point: `transmute-family` exists to be "a genuine
 * sink" (issue #8's own words). A lossless converter is a renaming tool and
 * removes every reason to care where a material comes from.
 */
export const TRANSMUTE_YIELD = 1 / 3;

/** Units of source material a transmutation consumes per cast. */
export const TRANSMUTE_INPUT_UNITS = 36;

/**
 * Six recipes, two per unlock, matching the ids in `progression.ts` exactly.
 *
 * Material names are `sim/materialData.ts`'s vocabulary and module ids are
 * `combat/moduleData.ts`'s; both are checked in `tests/ids.test.ts`, because the
 * plain-string decoupling this file keeps is only safe with that check — a
 * renamed module would otherwise leave a recipe that consumes materials and
 * produces nothing, which is this issue with extra steps.
 *
 * The two basic outputs are kinetic modules on purpose: kinetic is the one
 * family `BASELINE_MODULE_FAMILIES` never gates, so a first craft at 12 Salvage
 * cannot produce a module the profile is not allowed to have.
 */
export const RECIPES: readonly RecipeDef[] = [
  {
    id: 'repair-frame',
    tier: 'basic',
    name: 'Patch a Frame',
    desc: 'Beat scrap iron and gravel into a counterweight frame.',
    inputs: { IRON: 18, GRAVEL: 12 },
    produces: 'module',
    output: { moduleId: 'kin_frame_counterweight' },
  },
  {
    id: 'reseat-module',
    tier: 'basic',
    name: 'Re-seat a Core',
    desc: 'Pack brass and coal dust around a striking core.',
    inputs: { BRASS: 14, COAL_DUST: 10, IRON: 6 },
    produces: 'module',
    output: { moduleId: 'kin_core_impact' },
  },
  {
    id: 'transmute-family',
    tier: 'transmute',
    name: 'Transmutation',
    desc: `Convert ${TRANSMUTE_INPUT_UNITS} units of one material into ${Math.floor(
      TRANSMUTE_INPUT_UNITS * TRANSMUTE_YIELD,
    )} of another. Most of it is lost.`,
    inputs: { ALCHEMICAL_DUST: 20 },
    produces: 'material',
    needs: ['fromMaterial', 'toMaterial'],
  },
  {
    id: 'refine-reagent',
    tier: 'transmute',
    name: 'Refine Reagent',
    desc: 'Boil salt and water down to brine worth carrying.',
    inputs: { SALT: 24, WATER: 30 },
    produces: 'material',
    output: { material: 'BRINE', units: 12 },
  },
  {
    id: 'fuse-weapons',
    tier: 'grafting',
    name: 'Graft',
    desc: 'Move an installed module from one weapon to another. Nothing is rolled.',
    inputs: { NANITE_DUST: 22, LIVING_RESIN: 16 },
    produces: 'transfer',
    needs: ['twoWeapons', 'module'],
  },
  {
    id: 'splice-trigger',
    tier: 'grafting',
    name: 'Splice',
    desc: "Reroll a weapon's modules. What you had is gone.",
    inputs: { ALCHEMICAL_DUST: 30, MERCURY: 18 },
    produces: 'reroll',
    needs: ['weapon'],
  },
];

export const RECIPE_BY_ID: Readonly<Record<string, RecipeDef>> = Object.fromEntries(
  RECIPES.map((r) => [r.id, r]),
);

// `TIER_UNLOCK` lived here and was deleted by #118's sweep. "For a locked row's
// price" — and the crafting panel has no locked rows: `craftingVM` builds from
// `availableRecipes`, which filters to what the profile owns, so an unearned
// tier is absent rather than dimmed. It was also three unlock ids written out
// by hand beside the table that already holds them, which is the second failure
// this sweep kept finding: a copy nothing reads cannot even be caught drifting.

/** The one call site of `Progression.unlockedRecipes()`. */
export function availableRecipes(prog: Progression): RecipeDef[] {
  const owned = new Set(prog.unlockedRecipes());
  return RECIPES.filter((r) => owned.has(r.id));
}

// -------------------------------------------------------------------- crafting

export interface CraftArgs {
  fromMaterial?: string;
  toMaterial?: string;
  weaponInstanceId?: string;
  toWeaponInstanceId?: string;
  moduleId?: string;
}

export interface CraftResult {
  ok: boolean;
  /** Always set. On failure it is the reason, in the words a button prints. */
  message: string;
  outcome?: CraftOutcome;
  /** What was actually consumed, so the log can say it. */
  spent?: Readonly<Record<string, number>>;
}

/**
 * Craft, at a rest node, from the carried pouch.
 *
 * Every refusal names its own cause, in this order: not here, not unlocked, no
 * such recipe, missing argument, cannot afford. The order is the order a player
 * can act on — telling someone they are short of iron when the real problem is
 * that they are not standing at a workbench sends them digging for nothing.
 *
 * Materials are debited only after every check passes, and `spendMaterials` is
 * all-or-nothing, so there is no path through this function that costs the
 * player anything and returns `ok: false`.
 */
export function craft(
  run: RunState,
  prog: Progression,
  recipeId: string,
  args: CraftArgs = {},
  atRestNode = false,
): CraftResult {
  if (!atRestNode) {
    return { ok: false, message: 'Crafting needs a rest node — find a workbench.' };
  }
  const recipe = RECIPE_BY_ID[recipeId];
  if (!recipe) return { ok: false, message: `no such recipe: ${recipeId}` };
  if (!prog.unlockedRecipes().includes(recipe.id)) {
    return { ok: false, message: `${recipe.name} is not unlocked — advance Salvage.` };
  }

  const cost: Record<string, number> = { ...recipe.inputs };
  let outcome: CraftOutcome;

  switch (recipe.produces) {
    case 'module': {
      const moduleId = recipe.output?.moduleId;
      if (!moduleId) return { ok: false, message: `${recipe.name} names no module` };
      outcome = { kind: 'module', moduleId };
      break;
    }
    case 'material': {
      if (recipe.needs?.includes('fromMaterial')) {
        const from = args.fromMaterial;
        const to = args.toMaterial;
        if (!from || !to) {
          return { ok: false, message: `${recipe.name} needs a material to convert, and one to convert it into.` };
        }
        if (from === to) {
          return { ok: false, message: 'Converting a material into itself costs everything and changes nothing.' };
        }
        cost[from] = (cost[from] ?? 0) + TRANSMUTE_INPUT_UNITS;
        outcome = {
          kind: 'material',
          material: to,
          units: Math.floor(TRANSMUTE_INPUT_UNITS * TRANSMUTE_YIELD),
        };
      } else {
        const material = recipe.output?.material;
        const units = recipe.output?.units ?? 0;
        if (!material || units <= 0) return { ok: false, message: `${recipe.name} names no output` };
        outcome = { kind: 'material', material, units };
      }
      break;
    }
    case 'reroll': {
      if (!args.weaponInstanceId) {
        return { ok: false, message: `${recipe.name} needs a weapon to work on.` };
      }
      outcome = { kind: 'reroll', weaponInstanceId: args.weaponInstanceId };
      break;
    }
    case 'transfer': {
      if (!args.weaponInstanceId || !args.toWeaponInstanceId || !args.moduleId) {
        return { ok: false, message: `${recipe.name} needs two weapons and a module to move.` };
      }
      if (args.weaponInstanceId === args.toWeaponInstanceId) {
        return { ok: false, message: 'A module cannot be grafted onto the weapon it is already in.' };
      }
      outcome = {
        kind: 'transfer',
        fromInstanceId: args.weaponInstanceId,
        toInstanceId: args.toWeaponInstanceId,
        moduleId: args.moduleId,
      };
      break;
    }
  }

  const short = shortfall(run, cost);
  if (short.length > 0) {
    const list = short.map((s) => `${s.material} ${s.have}/${s.need}`).join(', ');
    return { ok: false, message: `Not enough material: ${list}.` };
  }

  // Every check has passed; this cannot fail, but a failed spend must still not
  // produce an outcome — the invariant is "nothing consumed on a refusal", and
  // an unreachable branch is the cheapest place to keep it true.
  if (!spendMaterials(run, cost)) {
    return { ok: false, message: 'Not enough material.' };
  }

  // A material outcome lands straight in the pouch: the caller cannot be trusted
  // to remember, and the cap has to apply to a conversion exactly as it applies
  // to a harvest, or transmutation becomes a way to exceed it.
  if (outcome.kind === 'material') {
    const got = harvestMaterial(run, outcome.material, outcome.units);
    outcome = { kind: 'material', material: outcome.material, units: got };
  }
  // A module outcome lands in the run's ledger for the same reason and by the
  // same argument (#107): `addModule` had no call site anywhere in `src/`
  // because recording an acquisition was always the integrator's line to
  // remember, and a line somebody has to remember is a line that eventually is
  // not written. The instruction returned below is still combat/'s to apply —
  // that is "socket this", this is "the run made one".
  if (outcome.kind === 'module') addModule(run, outcome.moduleId);

  return { ok: true, message: `${recipe.name} — ${describeOutcome(outcome)}`, outcome, spent: cost };
}

export function describeOutcome(o: CraftOutcome): string {
  switch (o.kind) {
    case 'module':
      return `made ${o.moduleId}`;
    case 'material':
      return o.units > 0 ? `made ${o.units} ${o.material}` : `pouch full — ${o.material} lost`;
    case 'reroll':
      return 'rerolled';
    case 'transfer':
      return `moved ${o.moduleId}`;
  }
}

// ------------------------------------------------------------------ view model

export interface RecipeVM {
  id: string;
  name: string;
  desc: string;
  tier: RecipeTier;
  /** "make" · "convert" · "move" — so the three tiers read as three verbs. */
  verb: string;
  /** `[{ material, need, have }]`, always the full input list, not only the gaps. */
  inputs: { material: string; need: number; have: number }[];
  /** Every input is in the pouch in sufficient quantity. */
  craftable: boolean;
  /** Needs arguments the panel must collect before the button does anything. */
  needs: readonly string[];
  /** Non-empty when the recipe is shown but cannot be run. */
  hint: string;
}

export interface CraftingVM {
  open: boolean;
  /** Non-empty when `open` is false. */
  hint: string;
  recipes: RecipeVM[];
  pouch: { material: string; units: number }[];
  pouchUsed: number;
  pouchCap: number;
}

/**
 * The crafting panel's whole model.
 *
 * `seenMaterials` is the codex record — issue #8's "inputs are materials the
 * codex has recorded". A recipe naming a material this profile has never touched
 * is withheld entirely rather than shown as unaffordable, because the player has
 * no way to act on a name they have never seen; once every input is recorded the
 * recipe appears and starts telling them what they are short of. Pass an empty
 * set to disable the codex gate.
 */
export function craftingVM(
  run: RunState,
  prog: Progression,
  seenMaterials: ReadonlySet<string>,
  atRestNode: boolean,
): CraftingVM {
  const unlocked = availableRecipes(prog);
  const gate = seenMaterials.size > 0;
  const recipes = unlocked
    .filter((r) => !gate || Object.keys(r.inputs).every((m) => seenMaterials.has(m)))
    .map((r): RecipeVM => {
      const inputs = Object.keys(r.inputs).map((m) => ({
        material: m,
        need: r.inputs[m],
        have: materialUnits(run, m),
      }));
      const craftable = inputs.every((i) => i.have >= i.need);
      return {
        id: r.id,
        name: r.name,
        desc: r.desc,
        tier: r.tier,
        verb: TIER_VERB[r.tier],
        inputs,
        craftable,
        needs: r.needs ?? [],
        hint: craftable
          ? ''
          : `Short: ${inputs
              .filter((i) => i.have < i.need)
              .map((i) => `${i.material} ${i.have}/${i.need}`)
              .join(', ')}`,
      };
    });

  const pouch = Object.keys(run.inventory.materials)
    .sort()
    .map((m) => ({ material: m, units: run.inventory.materials[m] }));

  return {
    open: atRestNode,
    hint: atRestNode ? '' : 'Crafting is hosted at rest nodes.',
    recipes,
    pouch,
    pouchUsed: pouchTotal(run),
    pouchCap: POUCH_CAP,
  };
}

/**
 * Every material any recipe names.
 *
 * **A test-facing export, and the only kind #118 kept as one.** `tests/ids.test.ts`
 * crosses this against the registered material names, so a recipe whose input
 * is a typo fails the suite instead of becoming an uncraftable row. The gate in
 * `tests/liveness.test.ts` excuses it under `TEST_FACING_EXPORTS` rather than
 * calling it dead, on the narrow ground that the test is the product: the
 * integrity of authored ids is not something a runtime caller could check.
 *
 * The docblock used to say "and the codex". The codex never read it. That
 * clause is what a listed-but-unearned claim looks like from the inside — it
 * made a dead export read as a live one for as long as nobody checked.
 */
export function recipeMaterials(): string[] {
  const out = new Set<string>();
  for (const r of RECIPES) {
    for (const m in r.inputs) out.add(m);
    if (r.output?.material) out.add(r.output.material);
  }
  return [...out];
}

/** Every module any recipe produces. Test-facing for the same reason, same gate entry. */
export function recipeModules(): string[] {
  const out = new Set<string>();
  for (const r of RECIPES) if (r.output?.moduleId) out.add(r.output.moduleId);
  return [...out];
}
